import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PairingCard } from './PairingCard';
import { PairingRow } from './PairingRow';
import type { RoundsController } from '@/pages/rounds/useRoundsController';

export function PairingsRoundCard({ ctrl }: { ctrl: RoundsController }) {
  const { t } = useTranslation('director');
  const {
    rounds,
    selectedRound,
    currentRound,
    editMode,
    setEditMode,
    setSwapSelection,
    setShowAddPairing,
    setShowDeletePrompt,
    godMode,
    isOwner,
    isChess,
    dragIndex,
    pendingAction,
    handleSetRoundDate,
    swapSelection,
    canUnpublish,
    allResultsConfirmed,
    gameConfig,
    resultOptions,
    handlePublish,
    handleUnpublish,
    handleCompleteRound,
    handleUncomplete,
    handleSetResult,
    handleConfirmResult,
    handleVoidResult,
    handleToggleInProgress,
    handleSwap,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
    handlePlayerSelect,
  } = ctrl;

  if (!currentRound) return null;

  // Round status label uses the translated value when present and falls back
  // to the raw enum (capitalized) so any new statuses still render legibly.
  const statusKey = `rounds_page.round_status.${currentRound.status}`;
  const translatedStatus = t(statusKey);
  const statusLabel =
    translatedStatus !== statusKey
      ? translatedStatus
      : currentRound.status.charAt(0).toUpperCase() + currentRound.status.slice(1);

  return (
    <Card>
      <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between pb-2 gap-2">
        <CardTitle className="text-lg">
          {t('rounds_page.round_n', { roundNumber: currentRound.roundNumber })}
        </CardTitle>
        <div className="flex items-center gap-2">
          {/* Story 096 — per-round date for the FIDE TRF (owner-only, chess). */}
          {isOwner && isChess && (
            <label className="flex items-center gap-1 text-xs text-muted-foreground">
              {t('rounds_page.round_date_label')}
              <input
                type="date"
                data-testid="round-date-input"
                value={currentRound.roundDate ?? ''}
                onChange={(e) =>
                  handleSetRoundDate(currentRound.id, e.target.value || null)
                }
                className="h-8 rounded-md border border-input bg-transparent px-2 text-sm dark:bg-input/30"
              />
            </label>
          )}
          <Badge variant="secondary" data-testid="round-status">
            {statusLabel}
          </Badge>
          {godMode && currentRound.status === 'paired' && (
            <>
              <Button
                variant={editMode ? 'destructive' : 'outline'}
                size="sm"
                className="h-8 w-8 p-0"
                data-testid="edit-mode-toggle"
                onClick={() => {
                  setEditMode(!editMode);
                  if (editMode) {
                    setSwapSelection(null);
                    setShowAddPairing(false);
                  }
                }}
              >
                {editMode ? <Eye className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
              </Button>
              {selectedRound === rounds.length - 1 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  data-testid="delete-round-button"
                  onClick={() => setShowDeletePrompt(true)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </>
          )}
          {isOwner && currentRound.status === 'paired' && (
            <Button
              size="sm"
              data-testid="publish-round-button"
              onClick={() => handlePublish(currentRound)}
            >
              {t('rounds_page.buttons.publish_round')}
            </Button>
          )}
          {currentRound.status === 'published' && (
            <>
              {godMode && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="unpublish-round-button"
                  onClick={() => handleUnpublish(currentRound)}
                  disabled={!canUnpublish}
                >
                  {t('rounds_page.buttons.unpublish')}
                </Button>
              )}
              {isOwner && (
                <Button
                  size="sm"
                  data-testid="complete-round-button"
                  onClick={() => handleCompleteRound(currentRound.id)}
                  disabled={!allResultsConfirmed}
                >
                  {t('rounds_page.buttons.complete_round')}
                </Button>
              )}
            </>
          )}
          {currentRound.status === 'completed' && godMode && (
            <Button
              size="sm"
              variant="outline"
              data-testid="uncomplete-round-button"
              onClick={handleUncomplete}
            >
              {t('rounds_page.buttons.uncomplete')}
            </Button>
          )}
        </div>
      </CardHeader>

      {/* --- Mobile: Card List --- */}
      <CardContent className="md:hidden" data-testid="pairing-cards">
        <div className="space-y-3">
          {currentRound.pairings.map((pairing, index) => (
            <PairingCard
              key={pairing.id}
              pairing={pairing}
              index={index}
              roundId={currentRound.id}
              roundStatus={currentRound.status}
              isFirst={index === 0}
              isLast={index === currentRound.pairings.length - 1}
              isPending={pendingAction?.pairingId === pairing.id ? pendingAction.type : null}
              gameConfig={gameConfig}
              resultOptions={resultOptions}
              onSwap={handleSwap}
              onSetResult={handleSetResult}
              onConfirmResult={handleConfirmResult}
              onVoidResult={godMode ? handleVoidResult : undefined}
              swapSelection={editMode ? swapSelection : null}
              onPlayerSelect={editMode ? handlePlayerSelect : undefined}
            />
          ))}
        </div>
      </CardContent>

      {/* --- Desktop: Table --- */}
      <CardContent className="hidden md:block" data-testid="pairings-table-container">
        <table className="w-full" data-testid="pairings-table">
          <thead>
            <tr className="border-b text-left text-sm text-muted-foreground">
              {currentRound.status === 'paired' && <th className="w-8 pb-2"></th>}
              <th className="w-16 pb-2">{t('rounds_page.table.table_header')}</th>
              <th className="pb-2">{gameConfig.colorNames.first}</th>
              <th className="pb-2">{gameConfig.colorNames.second}</th>
              {(currentRound.status === 'published' ||
                currentRound.status === 'completed') && (
                <th className="pb-2 text-center">{t('rounds_page.table.result_header')}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {currentRound.pairings.map((pairing, index) => (
              <PairingRow
                key={pairing.id}
                pairing={pairing}
                index={index}
                roundId={currentRound.id}
                roundStatus={currentRound.status}
                isDraggable={currentRound.status === 'paired'}
                isDragging={dragIndex === index}
                isFirst={index === 0}
                isLast={index === currentRound.pairings.length - 1}
                gameConfig={gameConfig}
                resultOptions={resultOptions}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onSwap={handleSwap}
                onSetResult={handleSetResult}
                onConfirmResult={handleConfirmResult}
                onVoidResult={godMode ? handleVoidResult : undefined}
                onToggleInProgress={handleToggleInProgress}
                isPending={pendingAction?.pairingId === pairing.id ? pendingAction.type : null}
                swapSelection={editMode ? swapSelection : null}
                onPlayerSelect={editMode ? handlePlayerSelect : undefined}
              />
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
